/**
 * Адреса клиента (D27).
 *
 * Собраны в одном месте и только здесь: путь, написанный строкой
 * в компоненте, — это ссылка, которая переживёт переименование
 * маршрута ровно до первого переименования.
 *
 * Строка запроса переносится при каждом переходе. На ней держится
 * вход по приглашению (D8): кампания и место приходят параметрами,
 * пока нет аутентификации, и потерять их при переходе на лист
 * значит выкинуть игрока из кампании.
 */

import { AUTH_CONFIRM_PATH, AUTH_RESET_PATH, INVITE_PATH } from '@rollcall/contracts'
import { TABS, type TabKey } from './tabs.js'

export const ROUTES = {
  /** Экран входа (D29): первое, что видно без параметров кампании. */
  start: '/',
  /**
   * Главная аккаунта: персонажи и кампании рядом (D8).
   *
   * До неё после входа человек попадал в один из двух разделов
   * и застревал в нём: перейти во второй можно было только выйдя
   * и войдя заново. Один и тот же человек бывает и мастером,
   * и игроком — значит, у аккаунта обязана быть точка, из которой
   * видно и то и другое.
   */
  home: '/home',
  characters: '/characters',
  newCharacter: '/characters/new',
  /**
   * Правка выборов (D62): тот же визард на сделанных ответах. Стоит
   * раньше вкладок листа в списке маршрутов: иначе «edit» прочитался
   * бы как имя вкладки.
   */
  editCharacter: '/characters/:id/edit',
  /**
   * Печатный лист (D64). Тоже раньше вкладок листа — по той же
   * причине, что и правка.
   */
  printCharacter: '/characters/:id/print',
  /** Лист без вкладки: открывается вкладкой по умолчанию. */
  sheet: '/characters/:id',
  sheetTab: '/characters/:id/:tab',
  /** Версия и указание источника SRD — отдельным экраном (D7). */
  about: '/about',
  /** Что поменялось в последних выпусках — человеческими словами. */
  whatsNew: '/whats-new',
  /* ── Аутентификация мастера (D8) ───────────────────────────────── */
  signIn: '/auth/sign-in',
  signUp: '/auth/sign-up',
  forgotPassword: '/auth/forgot',
  /**
   * Два адреса приходят из писем, и потому объявлены не здесь,
   * а в contracts: ссылку собирает сервер, а открывает её клиент —
   * это граница процесса (CLAUDE.md, п. 6). Сюда они втянуты, чтобы
   * маршруты по-прежнему лежали в одном месте.
   */
  confirmEmail: AUTH_CONFIRM_PATH,
  resetPassword: AUTH_RESET_PATH,
  /* ── Кампании (D8) ─────────────────────────────────────────────── */
  /** Свои записи контента (D53): список, новая, правка. */
  content: '/content',
  newContent: '/content/new',
  contentRecord: '/content/:id',
  /** Список кампаний мастера. */
  campaigns: '/campaigns',
  /** Одна кампания: места и очередь заявок. */
  campaign: '/campaigns/:id',
  /**
   * Переход по ссылке-приглашению. Путь тоже из contracts: ссылку
   * собирает сервер, а открывает её клиент (D38).
   */
  join: INVITE_PATH,
} as const

export function startPath(): string {
  return ROUTES.start
}

export function homePath(): string {
  return ROUTES.home
}

export function charactersPath(): string {
  return ROUTES.characters
}

export function newCharacterPath(): string {
  return ROUTES.newCharacter
}

export function editCharacterPath(id: string): string {
  return `/characters/${encodeURIComponent(id)}/edit`
}

export function printCharacterPath(id: string): string {
  return `/characters/${encodeURIComponent(id)}/print`
}

export function sheetPath(id: string, tab: TabKey): string {
  return `/characters/${encodeURIComponent(id)}/${tab}`
}

export function aboutPath(): string {
  return ROUTES.about
}

export function whatsNewPath(): string {
  return ROUTES.whatsNew
}

export function signInPath(): string {
  return ROUTES.signIn
}

export function signUpPath(): string {
  return ROUTES.signUp
}

export function forgotPasswordPath(): string {
  return ROUTES.forgotPassword
}

export function contentPath(): string {
  return ROUTES.content
}

export function newContentPath(): string {
  return ROUTES.newContent
}

export function contentRecordPath(id: string): string {
  return `/content/${encodeURIComponent(id)}`
}

export function campaignsPath(): string {
  return ROUTES.campaigns
}

export function campaignPath(id: string): string {
  return `/campaigns/${encodeURIComponent(id)}`
}

export function joinPath(): string {
  return ROUTES.join
}

/** Адрес вместе с текущей строкой запроса. */
export function withSearch(path: string, search: string): string {
  if (search === '' || search === '?') return path
  return `${path}${search.startsWith('?') ? search : `?${search}`}`
}

export function isTab(value: string | undefined): value is TabKey {
  return value !== undefined && (TABS as readonly string[]).includes(value)
}
